import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import settings from '../assets/settings.png';
import copytocopy from '../assets/copytocopy.png';
import QRCode from 'react-qr-code';

// Ağlar tanımı
const networks = {
  bsc: {
    name: 'Binance Smart Chain',
    rpcUrl: 'https://bsc-dataseed.binance.org/',
    chainId: 56,
    nativeSymbol: 'BNB',
    coingeckoId: 'binancecoin',
  },
  ethereum: {
    name: 'Ethereum',
    rpcUrl: 'https://rpc.ankr.com/eth',
    chainId: 1,
    nativeSymbol: 'ETH',
    coingeckoId: 'ethereum',
  },
  polygon: {
    name: 'Polygon',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    nativeSymbol: 'MATIC',
    coingeckoId: 'polygon',
  },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [network, setNetwork] = useState('bsc');
  const [address, setAddress] = useState('');
  const [balance, setBalance] = useState('');
  const [tokens, setTokens] = useState([]);
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [nativePrice, setNativePrice] = useState(0);
  const [totalValueUsd, setTotalValueUsd] = useState(0);
  const [selectedToken, setSelectedToken] = useState(null);
  const [showReceive, setShowReceive] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [sendToken, setSendToken] = useState('');
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');

  const handleTokenClick = (token) => {
    setSelectedToken(token);
    setSendToken(token.contractAddress);
  };

  const settingsMenu = () => {
    setShowSettings(!showSettings);
  };

  useEffect(() => {
    const mnemonic = localStorage.getItem('wallet_mnemonic');
    if (!mnemonic) return navigate('/');

    const net = localStorage.getItem('wallet_network') || 'bsc';
    setNetwork(net);

    const wallet = ethers.Wallet.fromPhrase(mnemonic);
    setAddress(wallet.address);

    const provider = new ethers.JsonRpcProvider(networks[net].rpcUrl);
    provider.getBalance(wallet.address).then((bal) => {
      setBalance(ethers.formatEther(bal));
    });

    const savedTokens = localStorage.getItem('wallet_tokens');
    if (savedTokens) {
      setTokens(JSON.parse(savedTokens));
    }

    const savedAutoUpdate = localStorage.getItem('wallet_auto_update');
    if (savedAutoUpdate !== null) {
      setAutoUpdate(savedAutoUpdate === 'true');
    }
  }, [navigate]);

  useEffect(() => {
    const tokensSafe = tokens.map((t) => ({
      ...t,
      balance: typeof t.balance === 'bigint' ? t.balance.toString() : t.balance,
      decimals: typeof t.decimals === 'bigint' ? t.decimals.toString() : t.decimals,
    }));

    localStorage.setItem('wallet_tokens', JSON.stringify(tokensSafe));
  }, [tokens]);

  useEffect(() => {
    localStorage.setItem('wallet_auto_update', autoUpdate.toString());
  }, [autoUpdate]);

  useEffect(() => {
    let interval;
    if (autoUpdate) {
      interval = setInterval(() => {
        updateAllTokens();
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [tokens, autoUpdate]);

  useEffect(() => {
    fetchPrices();
  }, [balance, tokens, network]); // Ağ bilgisini de dinle

  const fetchPrices = async () => {
    try {
      const native = networks[network];
      const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${native.coingeckoId}&vs_currencies=usd`);
      const data = await res.json();
      setNativePrice(data[native.coingeckoId].usd);

      let tokenTotal = 0;

      for (const token of tokens) {
        const resToken = await fetch(`https://api.coingecko.com/api/v3/simple/token_price/${native.coingeckoId}?contract_addresses=${token.contractAddress}&vs_currencies=usd`);
        const dataToken = await resToken.json();
        const price = dataToken[token.contractAddress?.toLowerCase()]?.usd;

        if (price) {
          tokenTotal += parseFloat(token.balance) * price;
        }
      }

      const total = (parseFloat(balance) * data[native.coingeckoId].usd) + tokenTotal;
      setTotalValueUsd(total);

    } catch (error) {
      console.error('Fiyat çekme hatası:', error);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handleDeleteWallet = () => {
    if (window.confirm('Cüzdanı silmek istediğine emin misin? Bu işlem geri alınamaz.')) {
      localStorage.clear();
      window.location.href = '/';
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(address);
    alert('Adres kopyalandı!');
  };

  const toggleAutoUpdate = () => {
    setAutoUpdate((prev) => !prev);
  };

  const handleShareAddress = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Etagram Wallet Adresim',
          text: `İşte benim Etagram Wallet adresim: ${address}`,
        });
      } catch (error) {
        console.error('Paylaşım iptal edildi', error);
      }
    } else {
      alert('Bu tarayıcı paylaşım desteklemiyor.');
    }
  };

  const handleAddToken = async () => {
    const contractAddress = prompt('Token Kontrat Adresi');
    if (!contractAddress) return alert('Boş kontrat adresi!');

    const lowerCaseAddress = contractAddress.toLowerCase();
    const isAlreadyAdded = tokens.some(token => token.contractAddress.toLowerCase() === lowerCaseAddress);
    if (isAlreadyAdded) {
      alert('Bu token zaten eklenmiş.');
      return;
    }

    try {
      const provider = new ethers.JsonRpcProvider(networks[network].rpcUrl);
      const erc20Abi = [
        'function name() view returns (string)',
        'function symbol() view returns (string)',
        'function decimals() view returns (uint8)',
        'function balanceOf(address) view returns (uint)',
      ];
      const tokenContract = new ethers.Contract(contractAddress, erc20Abi, provider);
      const name = await tokenContract.name();
      const symbol = await tokenContract.symbol();
      const decimals = await tokenContract.decimals();
      const rawBalance = await tokenContract.balanceOf(address);
      const formattedBalance = ethers.formatUnits(rawBalance, decimals);

      const newToken = { name, symbol, decimals, balance: formattedBalance, contractAddress };
      setTokens((prev) => [...prev, newToken]);
      alert(`${symbol} (${name}) başarıyla eklendi!`);
    } catch (error) {
      console.error('Token ekleme hatası:', error);
      alert('Token bilgileri alınamadı. Adresi kontrol et.');
    }
  };

  const updateAllTokens = async () => {
    try {
      const mnemonic = localStorage.getItem('wallet_mnemonic');
      const wallet = ethers.Wallet.fromPhrase(mnemonic);
      const provider = new ethers.JsonRpcProvider(networks[network].rpcUrl);

      const updatedTokens = await Promise.all(
        tokens.map(async (token) => {
          try {
            const erc20Abi = ['function balanceOf(address) view returns (uint)'];
            const contract = new ethers.Contract(token.contractAddress, erc20Abi, provider);
            const rawBalance = await contract.balanceOf(wallet.address);
            const formattedBalance = ethers.formatUnits(rawBalance, token.decimals);
            return { ...token, balance: formattedBalance };
          } catch (error) {
            console.error(`Token güncelleme hatası (${token.symbol}):`, error);
            return token; // Güncellenemeyen token'ı olduğu gibi döndür
          }
        })
      );
      setTokens(updatedTokens);
      console.log('Tüm tokenler güncellendi.');
    } catch (error) {
      console.error('Tokenler güncellenemedi:', error);
    }
  };

  const handleDeleteToken = (token) => {
    if (window.confirm(`${token.symbol} tokenı silinsin mi?`)) {
      setTokens((prevTokens) => prevTokens.filter((t) => t.contractAddress !== token.contractAddress));
    }
  };

  const handleSend = async () => {
    try {
      const mnemonic = localStorage.getItem('wallet_mnemonic');
      const provider = new ethers.JsonRpcProvider(networks[network].rpcUrl);
      const signer = ethers.Wallet.fromPhrase(mnemonic, provider);

      const nativeSymbol = networks[network].nativeSymbol;

      if (sendToken === nativeSymbol) {
        const tx = await signer.sendTransaction({
          to: sendTo,
          value: ethers.parseEther(sendAmount),
        });
        alert(`İşlem gönderildi! Tx Hash: ${tx.hash}`);
      } else {
        const erc20Abi = ['function transfer(address to, uint amount) returns (bool)'];
        const tokenContract = new ethers.Contract(sendToken, erc20Abi, signer);
        const tokenInfo = tokens.find(t => t.contractAddress === sendToken);
        const decimals = tokenInfo?.decimals || 18;
        const amount = ethers.parseUnits(sendAmount, decimals.toString());
        const tx = await tokenContract.transfer(sendTo, amount);
        alert(`Token gönderildi! Tx Hash: ${tx.hash}`);
      }
      setShowSend(false);
      setSendTo('');
      setSendAmount('');
      setSendToken(selectedToken?.contractAddress || nativeSymbol); // Gönderim sonrası seçimi sıfırla
    } catch (error) {
      console.error('Gönderme hatası:', error);
      alert('Gönderme işlemi başarısız.');
    }
  };

  return (
    <div className="dasboard-desing">
      <div className='dasboard-head'>
        <div><strong>Adres:</strong> {address.slice(0, 6)}...{address.slice(-4)}
          <img className='copytocopy' title='Copy' onClick={handleCopyAddress} src={copytocopy} alt="Copy" />
        </div>
        <div><strong>Bakiye:</strong> {balance} {networks[network].nativeSymbol}</div>
        <div><strong>Toplam Değer:</strong> ${totalValueUsd.toFixed(2)} USD</div>
        <div className='dasboard-head-settings' onClick={settingsMenu}><img src={settings} alt="Settings" /></div>
        {showSettings && (
          <div id="dasboard-head-setting">
            <ul>
              <li><button onClick={handleLogout}>Çıkış Yap</button></li>
              <li><button onClick={handleDeleteWallet}>Cüzdanı Sil</button></li>
              <li>
                <button onClick={toggleAutoUpdate} className={autoUpdate ? 'bg-green-600' : 'bg-gray-400'}>
                  {autoUpdate ? 'Otomatik Güncelle: Açık' : 'Otomatik Güncelle: Kapalı'}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      <div className='dashboard-token-list'>
        <h2>Token Listesi</h2>
        {tokens.length === 0 ? (
          <div>Henüz ekli token yok.</div>
        ) : (
          <ul>
            {tokens.map((token) => (
              <li key={token.contractAddress} onClick={() => handleTokenClick(token)}>
                <div className='dashboard-token-data'>
                  <span className="font-semibold">{token.symbol} ({token.name})</span>
                  <span>{token.balance}</span>
                </div>
                {<div className='up-del-btn'>
                  {/* <button onClick={() => handleUpdateToken(token)}>Güncelle</button> */} {/* handleUpdateToken kaldırıldı */}
                  <button onClick={() => handleDeleteToken(token)}>Sil</button>
                </div>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {selectedToken && (
        <div className="modal-overlay">
          <div className="modal-content">
            {!showReceive ? (
              <>
                <h2>{selectedToken.symbol} ({selectedToken.name})</h2>
                <p>Bakiye: {selectedToken.balance}</p>

                <div className="modal-buttons">
                  <button onClick={() => setShowReceive(true)}>Al</button>
                  <button onClick={() => setShowSend(true)}>Gönder</button>
                  {/* <button onClick={() => setShowSwap(true)}>Takas</button> */} {/* Takas özelliği kaldırıldı */}
                </div>

                <button onClick={() => setSelectedToken(null)}>Kapat</button>
              </>
            ) : (
              <>
                <h2>Al ({selectedToken.symbol})</h2>
                <p>Adresin:</p>
                <p style={{ wordBreak: 'break-all' }}>{address}</p>

                {/* QR kodu gösterilecek */}
                <QRCode value={address} size={200} />

                <div className="modal-buttons">
                  <button onClick={handleCopyAddress}>Adres Kopyala</button>
                  <button onClick={handleShareAddress}>Adres Paylaş</button>
                </div>

                <button onClick={() => setShowReceive(false)}>Geri</button>
              </>
            )}
          </div>
          {showSend && (
            <div className="modal-send">
              <h2>Gönder</h2>
              <label>Alıcı Adresi:</label>
              <input type="text" value={sendTo} onChange={(e) => setSendTo(e.target.value)} />

              <label>Miktar:</label>
              <input type="text" value={sendAmount} onChange={(e) => setSendAmount(e.target.value)} />

              <label>Token Seç:</label>
              <select value={sendToken} onChange={(e) => setSendToken(e.target.value)}>
                <option value={networks[network].nativeSymbol}>{networks[network].name} ({networks[network].nativeSymbol})</option>
                {tokens.map((token) => (
                  <option key={token.contractAddress} value={token.contractAddress}>
                    {token.symbol} ({token.name})
                  </option>
                ))}
              </select>

              <button onClick={handleSend}>Gönder</button>
              <button onClick={() => setShowSend(false)}>Kapat</button>
            </div>
          )}
        </div>
      )}

      <div>
        <button className='yeni-token-ekle-btn' onClick={handleAddToken}>Yeni Token Ekle</button>
      </div>
    </div>
  );
};

export default Dashboard;